import * as React from 'react';
import './../App.css';
import glamorous from 'glamorous';
import moment from 'moment';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

// Single source of truth for the standard (non-compact) footer's own height - mirrors
// MainDrawer.tsx's own HEADER_HEIGHT export, consumed the same way (QuickViewDrawer.tsx
// sizes itself to stop above the footer rather than overlapping it).
export const FOOTER_HEIGHT = '51px';

// tslint:disable-next-line:no-any
const Footer = glamorous.footer<{theme?: any; compact?: boolean; leftInset?: string}> ((props) => (
  props.compact
    ? {
        padding: '0',
        textAlign: 'center',
        display: 'flex',
        flexFlow: 'column wrap',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        gap: '2px',
      }
    : {
        // position: 'fixed',
        // right: 0,
        // bottom: 0,
        // left: 0,
        padding: '10px',
        // Explicit request: the left edge lines up with the page content instead of starting
        // under the sidebar - leftInset is the sidebar's own current width (MainDrawer.tsx's
        // FULL_WIDTH/SEMI_WIDTH, passed down from AppContent.tsx's dockMode, same calc already
        // used there for AiChatMinimizedBar's own leftInset), so this follows the sidebar's
        // expanded/collapsed state rather than a value that would only be right for one of them.
        paddingLeft: `calc(${props.leftInset || '0px'} + 20px)`,
        textAlign: 'center',
        display: 'flex',
        flexFlow: 'row wrap',
        // Explicit request: version/copyright on the left, the version tag/site link/GoDaddy
        // seal (all identity/trust signals) grouped together on the right - was one centered
        // cluster before, which read as cramped and undifferentiated.
        justifyContent: 'space-between',
        alignItems: 'center',
        boxSizing: 'border-box',
        minHeight: FOOTER_HEIGHT,
        borderTop: '1px solid ' + props.theme.NeutralColors.BackgroundGrey3,
        backgroundColor: props.theme.NeutralColors.BackgroundWhite,
        margin: '0 -10px'
      }
));

// tslint:disable-next-line:no-any
const FooterLink = glamorous.a<{theme?: any; compact?: boolean}> ((props) => (
  props.compact
    ? {
        textDecoration: 'none',
        color: '#8A8A8A',
        fontSize: '11px',
        display: 'inline',
      }
    : {
        textDecoration: 'none',
        // Explicit request: matches FooterLabel's own tone/size now that this sits beside the
        // version tag and GoDaddy seal as a trust line, not competing for attention on its own.
        color: props.theme.BrandColors.TextSecondary,
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        lineHeight: props.theme.LineHeight.Small,
      }
));

// tslint:disable-next-line:no-any
const FooterLabel = glamorous.span<{theme?: any; compact?: boolean}> ((props) => (
  props.compact
    ? {
        fontSize: '11px',
        textAlign: 'center',
        display: 'inline',
        color: '#8A8A8A',
      }
    : {
        fontSize: '12px',
        lineHeight: props.theme.LineHeight.Small,
        // Explicit request: flush left, not centered within its own flex item.
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        color: props.theme.BrandColors.TextSecondary,
      }
));

// tslint:disable-next-line: no-any
const FooterLabelWrapper = glamorous.div<{theme?: any; compact?: boolean}> ((props) => (
  props.compact
    ? {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'center',
        padding: '0',
      }
    : {
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px'
      }
));

// Explicit request: groups the version tag, site link and GoDaddy seal on the footer's right
// side (all identity/trust signals), replacing the single centered cluster everything used to
// sit in. Non-compact only - compact's own single-line layout is unchanged.
// tslint:disable-next-line: no-any
const FooterTrustWrapper = glamorous.div<{theme?: any}> (() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '0 10px',
}));

// Explicit request: a small monospace pill for the app version, standing on its own instead of
// being folded into the copyright sentence.
// tslint:disable-next-line: no-any
const FooterVersionTag = glamorous.span<{theme?: any}> ((props) => ({
  fontFamily: 'monospace',
  fontSize: '11px',
  padding: '2px 6px',
  borderRadius: '4px',
  border: '1px solid ' + props.theme.NeutralColors.BackgroundGrey3,
  color: props.theme.BrandColors.TextSecondary,
}));

// Explicit request: a muted separator between the version tag and the site link.
// tslint:disable-next-line: no-any
const FooterSeparator = glamorous.span<{theme?: any}> ((props) => ({
  color: props.theme.BrandColors.TextTertiary,
  fontSize: '12px',
}));

// Explicit request: a short vertical divider between the site link and the GoDaddy seal.
// tslint:disable-next-line: no-any
const FooterDivider = glamorous.span<{theme?: any}> ((props) => ({
  display: 'inline-block',
  width: '1px',
  height: '18px',
  backgroundColor: props.theme.NeutralColors.BackgroundGrey3,
}));

// Explicit request: the footer's own version label no longer tracks the backend's
// /api/query/currentversion response (VersionUpdateLibrary.checkForUpdate in
// public/assets/js/JsFunctions.js still runs and writes sessionStorage.currentVersion, but
// nothing reads it anymore) - hardcoded here instead, bumped by hand each release.
const CURRENT_VERSION = '2026.16';

export interface AppFooterProps {
  /** Renders a single centered 11px line without the border/background - use inside a narrow container instead of the standard full-width page footer. */
  compact?: boolean;
  /** Standard (non-compact) footer only - left padding offset matching the nav sidebar's
   * current width (MainDrawer.tsx's FULL_WIDTH/SEMI_WIDTH), so the copyright text lines up
   * with the page content instead of starting under the sidebar. Omit (or '0px') for a closed/
   * no sidebar state. */
  leftInset?: string;
}

export class AppFooter extends React.Component<AppFooterProps> {

  componentDidMount() {
    if (!document.querySelector('script[src*="seal.godaddy.com"]')) {
      var addScript = document.createElement('script');
      addScript.setAttribute('src', 'https://seal.godaddy.com/getSealBasic?sealID=TMkaFJpDa6Abub0CDqMYXdqLs5Q5qEiK2VPxAiOUdUhUE6ekWlraibHijmTZ');
      document.body.appendChild(addScript);
    }
  }

  render() {

    const currentYear = moment().format('YYYY');
    const currentVersion = CURRENT_VERSION;
    const compact = this.props.compact;

    const copyrightText = `© 2011–${currentYear} RedRain Corporation. All rights reserved.`;

    // Explicit bug report: measured 55px, not 51px - -5px wasn't enough to offset the zoomed
    // seal's own extra height within FOOTER_HEIGHT's budget. -7px closes that 4px gap.
    const siteSeal = (
      <span id="siteseal" style={{ padding: '0', display: 'inline-block', zoom: 0.45, margin: '-7px 0' }}/>
    );
    const siteLink = (
      <FooterLink compact={compact} href="https://www.redraincorp.com" target="_blank" rel="noopener noreferrer">
        redraincorp.com
        {!compact && <OpenInNewIcon style={{ fontSize: 13 }} />}
      </FooterLink>
    );

    // Compact's own single centered line is unchanged - only the standard footer splits the
    // version tag/site link/seal out into their own right-aligned group (see Footer/
    // FooterTrustWrapper).
    return compact ? (
        <Footer compact={compact}>
          <FooterLabelWrapper compact={compact}>
            <FooterLabel compact={compact}>{copyrightText}</FooterLabel>
            {siteLink}
          </FooterLabelWrapper>
          {siteSeal}
        </Footer>
    ) : (
        <Footer compact={compact} leftInset={this.props.leftInset}>
          <FooterLabelWrapper compact={compact}>
            <FooterLabel compact={compact}>{copyrightText}</FooterLabel>
          </FooterLabelWrapper>
          <FooterTrustWrapper>
            <FooterVersionTag>v{currentVersion}</FooterVersionTag>
            <FooterSeparator>&middot;</FooterSeparator>
            {siteLink}
            <FooterDivider />
            {siteSeal}
          </FooterTrustWrapper>
        </Footer>
    );
  }
}
